import { Spinner } from "@npco/zeller-design-system";
import * as Illustration from "@npco/zeller-design-system/illustration";
import { ADT } from "pb.adt";

import { formatCurrency } from "../../../../@lib-i18n/format-currency";
import { Layout } from "../../shared/layout";
import { PageTemplate } from "../../shared/page-template";
import { Prompt } from "../../shared/prompt";
import { Page } from "./page";

const defaultLayoutProps = {
	actionsStyle: "full-width",
	logo: true,
} satisfies Layout.Props;

export function render(page: Page) {
	return ADT.switch(page, {
		Initialising: PageTemplate({
			image: <Spinner />,
			title: "Connecting",
			intermediate: true,
		}),
		InitialisingPayment: PageTemplate({
			image: <Spinner />,
			title: "Initialising Payment",
			intermediate: true,
		}),
		AwaitProcessing: PageTemplate({
			image: <Spinner />,
			title: "Processing Payment",
			intermediate: true,
		}),
		AwaitAccountSelection: PageTemplate({
			image: <Illustration.TerminalT2Application />,
			title: "Waiting for Account selection",
			intermediate: true,
		}),
		AwaitApplicationSelection: PageTemplate({
			image: <Illustration.TerminalT2Application />,
			title: "Waiting for Card Application selection",
			intermediate: true,
		}),
		AwaitPaymentMethod: PageTemplate({
			image: <Illustration.TerminalT2TapNow />,
			title: "Waiting for Card or Device to be presented",
			intermediate: true,
		}),
		AwaitPinEntry: PageTemplate({
			image: <Illustration.TerminalT2Pin />,
			title: "Waiting for Card PIN entry",
			intermediate: true,
		}),
		AwaitSignature: PageTemplate({
			image: <Illustration.TerminalT2Signature />,
			title: "Waiting for Signature entry",
			intermediate: true,
		}),
		AwaitTipEntry: ({ onClose, onSkip, cancelling }) => (
			<Layout
				{...defaultLayoutProps}
				onClose={onClose}
				pending={cancelling ? "close" : undefined}
				actions={[{ label: "Skip", onClick: onSkip }]}
			>
				<Prompt
					image={<Illustration.TerminalT2Tip />}
					title="Waiting for Tip"
				/>
			</Layout>
		),
		DisplayPaymentMessage: PageTemplate({
			image: <Illustration.TerminalT2Application />,
			title: "Card Payment Message pending",
			intermediate: true,
		}),
		SignatureSubmitted: ({
			signature,
			onClose,
			onApprove,
			onDecline,
			cancelling,
		}) => (
			<Layout
				{...defaultLayoutProps}
				onClose={onClose}
				pending={cancelling ? "close" : undefined}
				actions={[
					{ label: "Approve", onClick: onApprove, primary: true },
					{ label: "Decline", onClick: onDecline },
				]}
			>
				<Prompt
					image={
						signature ? (
							<img
								alt="customer signature"
								className="dark:invert w-[180px] h-[180px]"
								src={signature}
							/>
						) : (
							<Illustration.TerminalT2Signature loop={false} />
						)
					}
					title="Verify Signature"
				/>
			</Layout>
		),
		TipProvided: ({ message = "Tip entered", onClose, cancelling }) => {
			const parts = message.split("\n");
			return (
				<Layout
					{...defaultLayoutProps}
					onClose={onClose}
					pending={cancelling ? "close" : undefined}
				>
					<Prompt
						image={<Illustration.TerminalT2Tip loop={false} />}
						/* v8 ignore next -- String.split always yields a defined element at index 0, so the fallback is unreachable */
						title={parts[0] ?? "Tip entered"}
						datapoint={parts[1]}
					/>
				</Layout>
			);
		},
		// response states
		Approved: ({ currency, totalCents, surchargeCents, tipCents, onDone }) => (
			<Layout
				{...defaultLayoutProps}
				timeout={{ onTimeout: onDone }}
				actions={[{ label: "Done", onClick: onDone, primary: true }]}
			>
				<Prompt
					image={<Spinner result="ok" />}
					title={`${formatCurrency({ amountInCents: totalCents, currency })} Approved`}
					message={[
						...(surchargeCents
							? [
									`Surcharge: ${formatCurrency({ amountInCents: surchargeCents, currency })}`,
								]
							: []),
						...(tipCents
							? [
									`Tip: ${formatCurrency({ amountInCents: tipCents, currency })}`,
								]
							: []),
					]}
				/>
			</Layout>
		),
		Declined: ({ title, subtitle, message, onRetry, onCancel }) => (
			<Layout
				{...defaultLayoutProps}
				timeout={{ onTimeout: onCancel }}
				actions={[
					{ label: "Try Again", onClick: onRetry, primary: true },
					{ label: "Cancel", onClick: onCancel },
				]}
			>
				<Prompt
					image={<Spinner result="error" />}
					title={title}
					subtitle={subtitle}
					message={message}
				/>
			</Layout>
		),
		Cancelled: PageTemplate({
			image: <Illustration.SuccessThumbsUp />,
			title: "Payment Cancelled",
		}),
		ErrorDisconnected: PageTemplate({
			image: <Illustration.Error />,
			title: "Terminal Error",
			message:
				"The Terminal has lost connection with this POS register. Please check the connection and try again. If this is the first time you're setting up the register, you may need to set up the terminal connection first.",
		}),
		ErrorInactivityTimeout: PageTemplate({
			image: <Illustration.ErrorBoatSinking loop={false} />,
			title: "Cancelled due to inactivity",
			message:
				"Purchase has been cancelled due to inactivity. Please try again.",
		}),
		ErrorNetwork: PageTemplate({
			image: <Illustration.ErrorBoatSinking loop={false} />,
			title: "Unable to connect to Zeller",
			message: "Please ensure this device is connected to the internet.",
			steps: [
				"Confirm your device is connected to your network.",
				"Check if the network is connected to the internet.",
			],
		}),
		ErrorOperation: PageTemplate({
			image: <Illustration.Error />,
			title: "Operation Failed",
			message:
				"An unrecoverable error occurred when processing this transaction. Please try again.",
		}),
		ErrorTerminalBusy: PageTemplate({
			image: <Illustration.ErrorDominoes loop={false} />,
			title: "Terminal Busy",
			message:
				"The terminal is currently busy. Please check the Terminal and try again.",
		}),
		ErrorTerminalUnreachable: PageTemplate({
			image: <Illustration.ErrorDominoes loop={false} />,
			title: "Failed to connect to Terminal",
			message:
				"Please check that your Zeller Terminal is connected to the Internet and try again.",
		}),
		ErrorTerminalMissing: PageTemplate({
			image: <Illustration.ErrorQuestionMark />,
			title: "No paired terminal found",
			message:
				"This device is not connected to a Zeller Terminal. Please pair it to process a payment.",
		}),
	});
}
